import axios from 'axios';
import { applyMiddleware, combineReducers, compose, createStore } from 'redux';
import thunk from 'redux-thunk';

import { entities, listNormalizer } from 'pages/store/schema';

import {
  byIdReducer,
  entitiesReducer,
  linksReducer,
  metaReducer,
  processingReducer,
} from './reducers/data';
import employeeShopsReducer, { EmployeeShopsState } from './reducers/employeeShops';
import filterReducer from './reducers/filter';
import createHubReducer, { HubState } from './reducers/hub';
import sortReducer from './reducers/sort';

/* eslint-disable no-underscore-dangle */
const composeEnhancers = window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__ || compose;

axios.interceptors.response.use(
  response => Promise.resolve(response),
  errors => {
    if (errors.message === 'Network Error') {
      window.flashNotifier(
        'warning',
        'Unable to contact server. You may be having a problem with your internet connection. If this continues contact support',
        15
      );
    }
    return Promise.reject(errors);
  }
);

const hasListNormalizer = (entity: string) => entity in listNormalizer;
const createEntityReducers = <T extends (str: string) => any>({
  reducer,
  reducerForNormalizedList = false,
}: {
  reducer: T;
  reducerForNormalizedList?: boolean;
}) =>
  Object.keys(entities).reduce((reducers, entity) => {
    if (!reducerForNormalizedList || (reducerForNormalizedList && hasListNormalizer(entity))) {
      return Object.assign({}, reducers, { [entity]: reducer(entity) });
    }
    return reducers;
  }, {});

const createRootReducer = () =>
  combineReducers({
    byId: combineReducers(createEntityReducers({ reducer: byIdReducer })),
    employeeShops: employeeShopsReducer,
    entities: combineReducers(createEntityReducers({ reducer: entitiesReducer })),
    filter: filterReducer,
    hub: createHubReducer(),
    links: combineReducers(createEntityReducers({ reducer: linksReducer })),
    meta: combineReducers(createEntityReducers({ reducer: metaReducer })),
    processing: combineReducers(createEntityReducers({ reducer: processingReducer })),
    sort: combineReducers(createEntityReducers({ reducer: sortReducer })),
  });

const enhancers = composeEnhancers(applyMiddleware(thunk.withExtraArgument({ axios })));

export interface StoreState {
  byId?: any[];
  employeeShops?: EmployeeShopsState;
  entities?: any;
  filter?: any;
  hub: HubState;
  links?: any;
  meta?: any;
  processing?: any;
  sort?: any;
}

export const initStore = (initialState: Partial<StoreState> = {}) =>
  createStore(createRootReducer(), initialState, enhancers);

const store = initStore();

export default store;

// This adds type inference to 'useSelector' and 'mapStateToProps'
declare module 'react-redux' {
  interface DefaultRootState extends StoreState {}
}
